import { useCallback, useState } from 'react';
import { FormProvider, SubmitHandler } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { errorMessages } from '~/constants/errorMessages.js';
import { formatDateToISO } from '~/helpers/utils.ts';
import FullPageLoader from '~/components/FullPageLoader/FullPageLoader.tsx';
import SuccessModal from '~/components/modals/SuccessModal/SuccessModal.tsx';
import ModalAlert from '~/components/modals/ModalAlert/ModalAlert';
import { useNavigate } from 'react-router';
import { useRetryAlert } from '../../hooks/useRetryAlert.ts';
import { useAppDispatch } from '~/store/appDispatch.ts';
import { RootState } from '~/store/store.ts';
import { postApplicationForm } from '~/store/processing-personal/thunks/applicationFormThunks.ts';
import { whyRevokeApplicationForm } from '~/store/processing-personal/thunks/whyRevokeApplicationFormThunks.ts';
import {
  TPersonalDataApplicationsSubmitForm,
  TPersonalDataWhyApplicationsForm,
} from '~/store/processing-personal/types.ts';
import { ApplicationSubmitFormValues } from '~/modules/processing-personal/PersonalDataApplicationForm/PersonalDataApplicationSubmit/types.ts';
import { ApplicationSignature } from '~/modules/processing-personal/PersonalDataApplicationForm/PersonalDataApplicationSubmit/ApplicationSignatureSubmit/ApplicationSignature.tsx';
import { ApplicationStepContent } from '~/modules/processing-personal/PersonalDataApplicationForm/PersonalDataApplicationSubmit/components/ApplicationStepContent.tsx';
import { documentPreviewPersonalData } from '~/store/processing-personal/thunks/documentPreviewPersonalThunks.ts';
import { StepNavigator } from '~/components/StepNavigator/StepNavigator.tsx';
import { useStepNavigation } from '~/components/StepNavigator/useStepNavigation.ts';
import { Box } from '@mui/material';
import { styles } from '~/modules/styles.ts';
import { useApplicationSubmitForm } from '../hooks/useApplicationSubmitForm.ts';
import { selectTokenData } from '~/store/users/selectors.ts';
import { PROCESSING_PERSONAL_SUBMIT_STEPS } from '~/modules/processing-personal/constants.ts';

export const ApplicationSubmit = () => {
  const dispatch = useAppDispatch();
  const { applicationId } = useSelector(selectTokenData) || {};
  const navigate = useNavigate();

  const { formData, loading: isLoading } = useSelector(
    (state: RootState) => state.processingPersonal
  );
  const normalizedFormData = formData ?? undefined;

  const [current, setCurrent] = useState<number>(1);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isOpenModalCongratulations, setIsOpenModalCongratulations] = useState<boolean>(false);
  const [isModalCongratulationsText, setIsModalCongratulationsText] = useState<string | null>(null);
  const [successRedirectPath, setSuccessRedirectPath] = useState<string>('/');

  const alert = useRetryAlert();
  const { next, prev, items } = useStepNavigation(
    current,
    setCurrent,
    PROCESSING_PERSONAL_SUBMIT_STEPS
  );

  const form = useApplicationSubmitForm(current, normalizedFormData);

  const handleCloseModal = (): void => {
    setIsModalOpen(false);
  };

  const closeModalCongratulations = (): void => {
    setIsOpenModalCongratulations(false);
    navigate(successRedirectPath);
  };

  const transformFormData = useCallback(
    (values: ApplicationSubmitFormValues): TPersonalDataApplicationsSubmitForm => {
      const { documentType, birthDate, issueDate, documentExpiryDate, ...rest } = values;

      return {
        ...rest,
        birthDate: formatDateToISO(birthDate),
        documentType: documentType?.value || null,
        issueDate: formatDateToISO(issueDate),
        documentExpiryDate: formatDateToISO(documentExpiryDate),
      };
    },
    []
  );

  const onSubmit: SubmitHandler<ApplicationSubmitFormValues> = async (values) => {
    if (isLoading) return;

    if (current === 1) {
      try {
        const dataForm = transformFormData(values);
        const res = await dispatch(
          postApplicationForm({ appId: applicationId, dataForm })
        ).unwrap();

        if (res.applicationDocumentId) {
          try {
            await dispatch(
              documentPreviewPersonalData({
                applicationId,
                applicationDocumentId: res.applicationDocumentId,
              })
            ).unwrap();
          } catch {
            alert.showError(errorMessages.SERVICE_UNAVAILABLE);
          }
        }

        next();
      } catch {
        alert.showError(errorMessages.SERVICE_UNAVAILABLE);
      }

      return;
    }

    if (current === 3) {
      const data: TPersonalDataWhyApplicationsForm = {
        disagreeWithProcessingPurpose: values.disagreeWithProcessingPurpose,
        disagreeWithPersonalDataScope: values.disagreeWithPersonalDataScope,
        otherReason: values.otherReason,
        otherReasonDescription: values.otherReasonDescription,
      };

      try {
        await dispatch(whyRevokeApplicationForm({ appId: applicationId, data })).unwrap();

        setSuccessRedirectPath('/');
        setIsOpenModalCongratulations(true);
        setIsModalCongratulationsText('Ваша заява успішно відкликана');
      } catch {
        alert.showError(errorMessages.SERVICE_UNAVAILABLE);
      }
    }
  };

  const handleOptionSignature = (): void => {
    if (form.formState.isSubmitting) return;

    if (current === 2) {
      setIsModalOpen(true);
    }
  };

  const handleSignatureSuccess = (successMessage: string): void => {
    setSuccessRedirectPath('/');
    setIsOpenModalCongratulations(true);
    setIsModalCongratulationsText(successMessage);
    setIsModalOpen(false);
  };

  const openSignatureModal = (): void => {
    setIsModalOpen(true);
  };

  const handleSignatureError = (errorMessage: string, errorCode?: string): void => {
    setIsModalOpen(false);
    alert.handleRetryableError(errorMessage, errorCode, openSignatureModal);
  };

  const handleQrTimeout = (): void => {
    setIsModalOpen(false);
    alert.handleTimeout(openSignatureModal);
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <Box id="applicationSubmitMain" sx={styles.mainFormPage}>
          <Box id="applicationSubmitContainer" sx={styles.mainFormPageContainer}>
            <StepNavigator id={'stepsContainer'} current={current} items={items} />
            <Box component="main" id="applicationSubmitContent" sx={styles.mainFormPageContent}>
              <ApplicationStepContent
                current={current}
                formData={normalizedFormData}
                isSubmitting={form.formState.isSubmitting || isModalOpen}
                onPrev={prev}
                onNext={next}
                onOptionSignature={handleOptionSignature}
              />
            </Box>
          </Box>

          <ApplicationSignature
            isModalOpen={isModalOpen}
            handleCloseModal={handleCloseModal}
            onSignatureSuccess={handleSignatureSuccess}
            onSignatureError={handleSignatureError}
            onQrTimeout={handleQrTimeout}
          />

          {isOpenModalCongratulations && (
            <SuccessModal
              isOpen={isOpenModalCongratulations}
              text={isModalCongratulationsText}
              onClose={closeModalCongratulations}
              showButton={true}
              onClick={closeModalCongratulations}
              buttonValue="Завершити"
              dataTestId="modalSuccess"
            />
          )}

          {alert.isOpen && (
            <ModalAlert
              isOpen={alert.isOpen}
              text={alert.text}
              onClose={alert.closeAlert}
              buttonValue={alert.retryAction ? 'Спробувати ще раз' : undefined}
              onClick={alert.retryAction ?? undefined}
              dataTestId="modalAlert"
            />
          )}

          {isLoading && <FullPageLoader />}
        </Box>
      </form>
    </FormProvider>
  );
};
